import gsap from 'gsap';
import { SpineAdapter } from './SpineAdapter.js';
import { gameConfig } from '../config/gameConfig.js';

/**
 * Default SpineAdapter implementation: GSAP tweens on a Pixi display object.
 * Replace with SpinePlayerAdapter without touching game components.
 */
export class SpriteTweenSpineAdapter extends SpineAdapter {
  /**
   * @param {import('pixi.js').Container} displayObject
   * @param {{
   *   animationController?: import('./AnimationController.js').AnimationController,
   *   idleMode?: 'static' | 'pulse',
   * }} [options]
   */
  constructor(displayObject, options = {}) {
    super();
    this.displayObject = displayObject;
    this.animationController = options.animationController ?? null;
    this.idleMode = options.idleMode ?? gameConfig.adapter.symbolIdleMode ?? 'static';
    this._mainTimeline = null;
    this._idleTimeline = null;
    this._baseScaleX = displayObject.scale.x;
    this._baseScaleY = displayObject.scale.y;
  }

  play(animationName) {
    this.stop();
    const cfg = gameConfig.adapter;
    const target = this.displayObject;

    if (animationName === gameConfig.animations.press) {
      this._mainTimeline = gsap.timeline({
        onComplete: () => this.animationController?.releaseTween?.(this._mainTimeline),
      });
      this._mainTimeline.to(target.scale, {
        x: this._baseScaleX * cfg.buttonPressScale,
        y: this._baseScaleY * cfg.buttonPressScale,
        duration: cfg.buttonTweenDuration,
        ease: 'power2.out',
      });
      this.animationController?.trackTween?.(this._mainTimeline);
      return;
    }

    if (animationName === gameConfig.animations.release) {
      this._mainTimeline = gsap.timeline({
        onComplete: () => this.animationController?.releaseTween?.(this._mainTimeline),
      });
      this._mainTimeline.to(target.scale, {
        x: this._baseScaleX,
        y: this._baseScaleY,
        duration: cfg.buttonTweenDuration,
        ease: 'power2.out',
      });
      this.animationController?.trackTween?.(this._mainTimeline);
      return;
    }

    if (animationName === gameConfig.animations.win) {
      this._mainTimeline = this._createWinTimeline(target, cfg, true);
      this.animationController?.trackTween?.(this._mainTimeline);
      return;
    }

    if (animationName === gameConfig.animations.idle) {
      this.setIdle();
      return;
    }

    this.setIdle();
  }

  /**
   * @param {import('pixi.js').Container} target
   * @param {typeof gameConfig.adapter} cfg
   * @param {boolean} loop
   */
  _createWinTimeline(target, cfg, loop) {
    const peak = cfg.winScalePeak ?? 1.12;
    const pop = cfg.winPopDuration ?? 0.26;
    const settle = cfg.winSettleDuration ?? 0.35;
    const wiggle = cfg.winRotationWiggleRad ?? 0.06;
    const shimmer = cfg.winShimmerAlphaMin ?? 0.9;

    const tl = gsap.timeline({
      repeat: loop ? -1 : 0,
      onComplete: () => this.animationController?.releaseTween?.(tl),
    });
    tl.to(target.scale, {
      x: this._baseScaleX * peak,
      y: this._baseScaleY * peak,
      duration: pop,
      ease: 'back.out(1.65)',
    })
      .to(
        target,
        { rotation: wiggle, duration: pop * 0.34, ease: 'sine.out' },
        `-=${pop * 0.22}`,
      )
      .to(target, { rotation: -wiggle * 0.68, duration: pop * 0.42, ease: 'sine.inOut' })
      .to(target, { rotation: 0, duration: pop * 0.36, ease: 'sine.out' })
      .to(target.scale, {
        x: this._baseScaleX,
        y: this._baseScaleY,
        duration: settle,
        ease: 'power3.inOut',
      })
      .to(
        target,
        {
          alpha: shimmer,
          duration: settle * 0.24,
          yoyo: true,
          repeat: 1,
          ease: 'sine.inOut',
        },
        `-=${settle * 0.5}`,
      );
    return tl;
  }

  setWin() {
    this.play(gameConfig.animations.win);
  }

  /** One full win tween (no loop) — used while cycling paylines. */
  setWinOnce() {
    this.stop();
    const cfg = gameConfig.adapter;
    this._mainTimeline = this._createWinTimeline(this.displayObject, cfg, false);
    this.animationController?.trackTween?.(this._mainTimeline);
  }

  /**
   * @param {string} animationName
   * @returns {number}
   */
  getClipDurationSec(animationName) {
    if (animationName !== gameConfig.animations.win) return 0;
    const cfg = gameConfig.adapter;
    const pop = cfg.winPopDuration ?? 0.26;
    const settle = cfg.winSettleDuration ?? 0.35;
    const probe = this._createWinTimeline(
      { scale: { x: 1, y: 1 }, rotation: 0, alpha: 1 },
      cfg,
      false,
    );
    const sec = probe.duration();
    probe.kill();
    return sec > 0 ? sec : pop + settle;
  }

  stop() {
    if (this._mainTimeline) {
      const tl = this._mainTimeline;
      this._mainTimeline = null;
      tl.kill();
      this.animationController?.releaseTween?.(tl);
    }
    if (this._idleTimeline) {
      const tl = this._idleTimeline;
      this._idleTimeline = null;
      tl.kill();
      this.animationController?.releaseTween?.(tl);
    }
    this.displayObject.alpha = 1;
    this.displayObject.rotation = 0;
    this.displayObject.scale.set(this._baseScaleX, this._baseScaleY);
  }

  setIdle() {
    this.stop();
    if (this.idleMode !== 'pulse') {
      this.displayObject.scale.set(this._baseScaleX, this._baseScaleY);
      this.displayObject.alpha = 1;
      return;
    }
    const cfg = gameConfig.adapter;
    const target = this.displayObject;
    this._idleTimeline = gsap.timeline({ repeat: -1, yoyo: true });
    this._idleTimeline.to(target.scale, {
      x: this._baseScaleX * cfg.idlePulseScale,
      y: this._baseScaleY * cfg.idlePulseScale,
      duration: cfg.idlePulseDuration,
      ease: 'sine.inOut',
    });
    this.animationController?.trackTween?.(this._idleTimeline);
  }

  reset() {
    this.stop();
    this.displayObject.rotation = 0;
    this.displayObject.scale.set(this._baseScaleX, this._baseScaleY);
    this.displayObject.alpha = 1;
  }
}
